import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { GET_ME } from '../queries/getMe';

/**
 * Does this person belong to more than one organisation?
 *
 * Which is the same question as "is there anything to switch between" — the switcher is hidden
 * for the large majority who belong to one, because an entry that always leads to a page with a
 * single option is noise.
 *
 * Reads `GET_ME`, which every other role hook already reads, so this costs no extra request: the
 * Apollo cache answers it.
 */
export const useHasMultipleOrganisations = (): boolean => {
  const { data, loading, error } = useQuery(GET_ME);
  if (loading || error) return false;
  return (data?.me?.organisations?.length ?? 0) > 1;
};

/**
 * Is the working organisation a supporter of somebody?
 *
 * The server derives this from live `Supporter` rows rather than an `Organisation.kind` column —
 * see `utils/rolePresets.ts` for why there is no such column. A supporter organisation has no
 * courses, no SKU tag group and no classes, so the class, reporting, attendance and partnership
 * entries in the nav lead to pages that can only ever be empty there.
 *
 * **This hides menu entries; it does not gate anything.** The pages keep their own checks. An
 * organisation that both runs classes and supports somebody would lose entries it could use —
 * none exists today, and the honest fix then is per-page rather than making this cleverer.
 */
export const useIsSupportingOrganisation = (): boolean => {
  const { data, loading, error } = useQuery(GET_ME);
  if (loading || error) return false;
  return data?.me?.organisation?.isSupportingOrganisation ?? false;
};

const CAN_MANAGE_ORPHANS = gql`
  query CanManageOrphanedAccounts {
    canManageOrphanedAccounts
  }
`;

/**
 * May this person see the signup queue?
 *
 * Its own query rather than a field on `GET_ME`, because one person holds the capability and
 * every other viewer would be paying for a column that is always false.
 */
export const useCanManageOrphanedAccounts = (): boolean => {
  const { data, loading, error } = useQuery(CAN_MANAGE_ORPHANS);
  if (loading || error) return false;
  return data?.canManageOrphanedAccounts === true;
};

const CAN_INVITE = gql`
  query CanInviteCongregants {
    canInviteCongregants
  }
`;

/**
 * May this person invite members of their own organisation?
 *
 * The server's own answer — `ORGANISATION: MANAGE`, which `PARTNER_ADMIN` holds in a supporter
 * organisation. Asked rather than inferred from a role name, so an organisation that invents its
 * own administrator role on `/roles` gets the menu entry with it.
 */
export const useCanInviteCongregants = (): boolean => {
  const { data, loading, error } = useQuery(CAN_INVITE);
  if (loading || error) return false;
  return data?.canInviteCongregants === true;
};
